export const PULL_TO_REFRESH_IGNORE_SELECTOR =
  'button, a, input, textarea, select, [contenteditable], [role="slider"], [data-pull-to-refresh-ignore]';

export interface ScrollBoundaryAdapter<TNode> {
  getParent: (node: TNode) => TNode | null;
  isVerticallyScrollable: (node: TNode) => boolean;
  getScrollTop: (node: TNode) => number;
  getDocumentScrollingElement: () => TNode | null;
  hasIgnoredAncestor: (node: TNode, selector: string) => boolean;
}

export interface ScrollBoundaryOptions<TNode> {
  target: TNode;
  adapter: ScrollBoundaryAdapter<TNode>;
  tolerancePx?: number;
}

export const isPullToRefreshBoundary = <TNode>({
  target,
  adapter,
  tolerancePx = 1,
}: ScrollBoundaryOptions<TNode>): boolean => {
  let node: TNode | null = target;
  const documentScroller = adapter.getDocumentScrollingElement();

  while (node) {
    if (node !== documentScroller && adapter.isVerticallyScrollable(node) && adapter.getScrollTop(node) > tolerancePx) {
      return false;
    }
    node = adapter.getParent(node);
  }

  return !documentScroller || adapter.getScrollTop(documentScroller) <= tolerancePx;
};

export const isPullToRefreshIgnoredTarget = <TNode>(target: TNode, adapter: ScrollBoundaryAdapter<TNode>): boolean => {
  return adapter.hasIgnoredAncestor(target, PULL_TO_REFRESH_IGNORE_SELECTOR);
};

export const browserScrollBoundaryAdapter: ScrollBoundaryAdapter<Node> = {
  getParent: (node) => node.parentNode,
  isVerticallyScrollable: (node) => {
    if (!(node instanceof HTMLElement) || node.scrollHeight <= node.clientHeight + 1) {
      return false;
    }

    const overflowY = window.getComputedStyle(node).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  },
  getScrollTop: (node) => (node instanceof Element ? node.scrollTop : 0),
  getDocumentScrollingElement: () => document.scrollingElement,
  hasIgnoredAncestor: (node, selector) => node instanceof Element && node.closest(selector) !== null,
};
